/* eslint-disable max-lines */
import React, { ReactElement, useContext, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  UncontrolledDropdown,
} from 'reactstrap';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import ModalContent from '../../../../components/Modals/Modal';
import TimeCardService from '../../../../services/TimeCard';
import PayrollService from '../../../../services/Payroll';
import DataTableWithoutHeaderAndPagination from '../../../../components/Core/DataTableWithoutHeaderAndPagination';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import { toast } from 'react-toastify';

interface navTab {
  name: string;
  visible: boolean;
  content: ReactElement;
}

interface PayrollOverviewCardInfoProps {
  weekStartDate: string;
  weekEndDate: string;
}

export const PayrollOverviewCardInfo = ({ weekStartDate, weekEndDate }: PayrollOverviewCardInfoProps) => {
  const { token, currentSchool }: any = useContext(AuthContext);

  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'ready'>('idle');
  const [toggleModal, setToggleModal] = useState(false);
  const [timecards, setTimecards] = useState([]);
  const [activeTab, setActiveTab] = useState<'pending' | 'denied' | 'approved' | 'reviewed'>('pending');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(new Date().getTime());

  const handleApprove = async (id: string) => {
    try {
      await TimeCardService(token).reviewApprove(id);
      toast.success('Timecard approved successfully!');
      setLastUpdatedAt(new Date().getTime());
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error approving timecard');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await TimeCardService(token).reviewReject(id);
      toast.success('Timecard rejected successfully!');
      setLastUpdatedAt(new Date().getTime());
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error rejecting timecard');
    }
  };

  const getDataTableByTab = (tab: string) => {
    const baseColumns = [
      {
        name: 'Name',
        grow: 2,
        route: (row: any) => `/users/${row?.user?.id}/profile`,
        cell: (row: any) => (
          <Link to={`/users/${row?.user?.id}/profile`}>
            <div className="d-flex align-items-center">
              <div style={{ width: '35px', height: '35px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                <img
                  src={row?.user?.picture}
                  alt="User Profile"
                  className="img-fluid"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <span className="ml-2">{`${row?.user?.firstName || '-'} ${row?.user?.lastName || '-'}`}</span>
            </div>
          </Link>
        ),
      },
    ];

    let tabStatus = [tab];
    if (tab === 'REVIEWED') {
      tabStatus = ['REVIEWED_REJECTED', 'REVIEWED_APPROVED'];
    }
    const filtered = timecards.filter((e: any) => tabStatus.includes(e.status));

    const uniqueDates: string[] = [];
    const start = moment(weekStartDate);
    const end = moment(weekEndDate);
    for (let d = start.clone(); d.isSameOrBefore(end, 'day'); d.add(1, 'day')) {
      if (d.day() === 0 || d.day() === 6) continue;
      uniqueDates.push(d.format('YYYY-MM-DD'));
    }

    const dateColumns = uniqueDates.map((date) => ({
      name: moment(date).format('MMM DD'),
      cell: (row: any) => {
        const times = row.attendancesByDate?.[date] || [];
        if (times.length === 0) return <div>-</div>;

        const grouped = [];
        for (let i = 0; i < times.length; i += 2) {
          if (i + 1 < times.length) {
            grouped.push(`${moment(times[i]).format('hh:mm a')} > ${moment(times[i + 1]).format('hh:mm a')}`);
          } else {
            grouped.push(moment(times[i]).format('hh:mm a'));
          }
        }

        return (
          <div>
            {grouped.map((t: string, i: number) => (
              <div key={i} className="mb-1">
                <Badge color="light" pill style={{ fontSize: '12px' }}>
                  {t}
                </Badge>
              </div>
            ))}
          </div>
        );
      },
      grow: 1,
      center: true,
    }));

    const columns = [
      ...baseColumns,
      ...dateColumns,
      ...[
        {
          name: 'Total Hours',
          grow: 1,
          cell: (row: any) => (row.workedHours != null ? row.workedHours : '—'),
        },
      ],
    ];

    if (tab === 'REVIEWED') {
      columns.push({
        name: 'Status',
        grow: 1,
        center: true,
        cell: (row: any) => {
          if (row.status === 'REVIEWED_APPROVED') {
            return <Badge color="success">Approved</Badge>;
          }
          if (row.status === 'REVIEWED_REJECTED') {
            return <Badge color="danger">Rejected</Badge>;
          }
          return null;
        },
      });
    } else {
      columns.push({
        name: 'Action',
        grow: 1,
        center: true,
        cell: (row: any) =>
          row.id ? (
            <>
              <Button style={{ border: 'none', boxShadow: 'none', padding: 0 }} onClick={() => handleApprove(row.id)}>
                <i className="fas fa-check text-success" />
              </Button>
              <Button style={{ border: 'none', boxShadow: 'none', padding: 0 }} onClick={() => handleReject(row.id)}>
                <i className="fas fa-ban text-danger" />
              </Button>
            </>
          ) : (
            <span className="text-muted">—</span>
          ),
      });
    }

    const data = filtered.map((tc: any) => {
      const attendancesByDate: Record<string, string[]> = {};

      uniqueDates.forEach((date) => {
        attendancesByDate[date] = (tc.attendances || [])
          .map((att: any) => att.attendance)
          .filter((att: any) => att?.createdAt && moment(att.createdAt).format('YYYY-MM-DD') === date)
          .map((att: any) => att.createdAt)
          .sort((a: string, b: string) => a.localeCompare(b));
      });

      return {
        ...tc,
        attendancesByDate,
      };
    });

    return (
      <DataTableWithoutHeaderAndPagination
        title=""
        columns={columns}
        data={data}
        loading={false}
        button={false}
        buttonUrl=""
        onChangeRowsPerPage={(() => {}) as any}
        onChangePage={(() => {}) as any}
      />
    );
  };
  const tabs: Record<string, navTab> = {
    pending: {
      name: 'Pending',
      content: getDataTableByTab('PENDING'),
      visible: true,
    },
    denied: {
      name: 'Denied',
      content: getDataTableByTab('DENIED'),
      visible: true,
    },
    approved: {
      name: 'Approved',
      content: getDataTableByTab('APPROVED'),
      visible: true,
    },
    reviewed: {
      name: 'Reviewed / Completed',
      content: getDataTableByTab('REVIEWED'),
      visible: true,
    },
  } as const;

  const customStyles = {
    navLink: {
      minHeight: '42px',
      paddingTop: '8px',
      cursor: 'pointer',
    },
  };

  const handleToggleModal = () => {
    setToggleModal(!toggleModal);
  };

  const openModalOnStatus = (statusKey: 'pending' | 'denied' | 'approved' | 'reviewed') => {
    setActiveTab(statusKey);
    setToggleModal(true);
  };

  const getTotalByStatus = (status: string) => {
    let tabStatus = [status.toUpperCase()];
    if (status === 'Reviewed / Completed' || status === 'reviewed') {
      tabStatus = ['REVIEWED_REJECTED', 'REVIEWED_APPROVED'];
    }
    return timecards.filter((e: any) => tabStatus.includes(e.status)).length || 0;
  };

  useEffect(() => {
    const loadTimecards = async () => {
      if (!weekStartDate || !weekEndDate || !currentSchool?.value) {
        return;
      }

      setStatus('loading');
      try {
        const result = await PayrollService(token).timecardListWithPending(currentSchool.value, {
          weekStartDate,
          weekEndDate,
          page: 1,
          limit: 99999,
          'filters[field]': 'status',
          'filters[value]': 'ACTIVE',
        });
        const rows = (result.data || []).map((item: any) => {
          const emp = item.employee || {};
          const tc = item.timecard;
          if (tc == null) {
            const pendingAtt = (item.pendingAttendances || []).map((a: any) => ({ attendance: a }));
            return {
              user: emp.user,
              status: 'PENDING',
              id: undefined,
              attendances: pendingAtt,
              workedHours: item.pendingWorkedHours ?? undefined,
            };
          }
          return {
            ...tc,
            user: emp.user,
          };
        });
        setTimecards(rows);
        setStatus('ready');
      } catch (error) {
        setStatus('error');
        toast.error('Error loading timecards');
      }
    };

    loadTimecards();
  }, [currentSchool, toggleModal, lastUpdatedAt, weekStartDate, weekEndDate, token]);

  const pendingCount = getTotalByStatus('pending');
  const deniedCount = getTotalByStatus('denied');
  const approvedCount = getTotalByStatus('approved');
  const reviewedCount = getTotalByStatus('reviewed');
  const totalCount = timecards.length;
  const needsActionCount = pendingCount + deniedCount;
  const isLoading = status === 'loading';

  const funnelTiles: Array<{
    key: 'pending' | 'denied' | 'approved' | 'reviewed';
    label: string;
    hint: string;
    color: string;
    count: number;
    goal?: boolean;
  }> = [
    { key: 'pending', label: 'Pending', hint: 'Awaiting staff', color: '#fb6340', count: pendingCount },
    { key: 'denied', label: 'Denied', hint: 'Needs correction', color: '#f5365c', count: deniedCount },
    { key: 'approved', label: 'Approved', hint: 'Awaiting director', color: '#3b82c4', count: approvedCount },
    {
      key: 'reviewed',
      label: 'Reviewed / Completed',
      hint: 'Finalized',
      color: '#2dce89',
      count: reviewedCount,
      goal: true,
    },
  ];

  return (
    <>
      <div style={{ background: '#fff', padding: '18px 0', borderRadius: 12 }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ gap: 8 }}>
          <span style={{ fontSize: 13, color: '#8898aa' }}>
            Timecard status{' '}
            <span style={{ color: '#adb5bd' }}>· for week of {moment(weekStartDate).format('MMM DD, YYYY')}</span>
          </span>
          <button
            type="button"
            onClick={handleToggleModal}
            style={{ border: 'none', background: 'transparent', color: '#525f7f', fontSize: 13, cursor: 'pointer' }}
          >
            {isLoading ? '—' : `${reviewedCount}/${totalCount} finalized`}
            <i className="fas fa-expand-alt ml-2" />
          </button>
        </div>
        <div className="d-flex align-items-stretch flex-wrap" style={{ gap: 10 }}>
          {funnelTiles.map((tile, i) => (
            <React.Fragment key={tile.key}>
              <button
                type="button"
                onClick={() => openModalOnStatus(tile.key)}
                aria-label={`${tile.label}: ${tile.count}`}
                style={{
                  flex: '1 1 160px',
                  minWidth: 150,
                  textAlign: 'left',
                  padding: '16px 18px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  border: `1.5px solid ${tile.goal ? tile.color : '#e9ecef'}`,
                  background: tile.goal ? '#e6f8f0' : '#fff',
                  boxShadow: tile.goal ? '0 4px 12px rgba(45,206,137,.18)' : 'none',
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        background: tile.color,
                        display: 'inline-block',
                      }}
                    />
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#525f7f' }}>{tile.label}</span>
                  </span>
                  {tile.goal && (
                    <Badge style={{ background: tile.color, color: '#fff' }} pill>
                      GOAL
                    </Badge>
                  )}
                </div>
                <div style={{ fontSize: 34, fontWeight: 800, color: tile.count === 0 ? '#8898aa' : tile.color }}>
                  {isLoading ? (
                    <span
                      style={{
                        height: 34,
                        width: 48,
                        borderRadius: 4,
                        background: '#e3e9f2',
                        display: 'inline-block',
                        animation: 'skeleton-loading 1.2s infinite linear alternate',
                      }}
                    />
                  ) : (
                    tile.count
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: '#8898aa' }}>{tile.hint}</div>
              </button>
              {i < funnelTiles.length - 1 && (
                <span className="d-none d-md-flex align-items-center" style={{ color: '#8898aa' }}>
                  <i className="fas fa-arrow-right" />
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
        {!isLoading && needsActionCount > 0 && (
          <div className="mt-3" style={{ fontSize: 13, color: '#f5365c', fontWeight: 600 }}>
            <i className="fas fa-circle mr-2" style={{ fontSize: 8, verticalAlign: 'middle' }} />
            {needsActionCount} timecard{needsActionCount > 1 ? 's' : ''} still need action this week
          </div>
        )}
      </div>
      <ModalContent title="Payroll Overview" modal={toggleModal} toggle={handleToggleModal} size="xl" fullscreen={true}>
        <Row>
          <Col xs="12">
            {isMobile && (
              <UncontrolledDropdown nav className="w-100 mb-2">
                <DropdownToggle className="p-0" nav>
                  <Button className="w-100">
                    <span>
                      {activeTab.toLocaleUpperCase()}
                      <span className="badge badge-info ml-1">{getTotalByStatus(activeTab)}</span>
                      <i className="fas fa-chevron-down float-right"></i>
                    </span>
                  </Button>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-arrow w-100">
                  {Object.keys(tabs).map((tab: string) => {
                    const currentTab: navTab = (tabs as any)[tab];
                    return (
                      <>
                        {currentTab.visible && (
                          <DropdownItem
                            tag={Link}
                            onClick={async (e) => {
                              e.preventDefault();
                              setActiveTab(tab as 'pending' | 'denied' | 'approved' | 'reviewed');
                            }}
                          >
                            <span>
                              {currentTab.name}
                              <span className="badge badge-info ml-1">{getTotalByStatus(tab)}</span>
                            </span>
                          </DropdownItem>
                        )}
                      </>
                    );
                  })}
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
            {!isMobile && (
              <Nav tabs>
                {Object.keys(tabs).map((tab: string, i: number) => {
                  const currentTab: navTab = (tabs as any)[tab];
                  return (
                    <>
                      {currentTab.visible && (
                        <NavItem className={`cursor-pointer ${isMobile ? 'col-6 p-0' : ''}`} key={`nav-item_${i}`}>
                          <NavLink
                            style={customStyles.navLink}
                            active={activeTab === tab}
                            onClick={() => setActiveTab(tab as 'pending' | 'denied' | 'approved' | 'reviewed')}
                          >
                            <span>
                              {currentTab.name}
                              <span className="badge badge-light ml-1">{getTotalByStatus(tab)}</span>
                            </span>
                          </NavLink>
                        </NavItem>
                      )}
                    </>
                  );
                })}
              </Nav>
            )}
          </Col>
          <Col xs="12">
            <TabContent activeTab={activeTab}>
              {Object.keys(tabs).map((tab: string) => {
                const currentTab: navTab = (tabs as any)[tab];
                return currentTab.visible && activeTab === tab && currentTab.content;
              })}
            </TabContent>
          </Col>
        </Row>
      </ModalContent>
    </>
  );
};
